import { enforceShape } from "@/src/lib/enforce-shape";
import { createLogger } from "@/src/lib/logging/logger";
import { stats } from "@/src/lib/logging/stats.server";
import { getAllItems } from "@/src/lib/pagination";
import { withRetry } from "@/src/lib/retry";
import { customCamelCase, customSnakeCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { Database } from "@/src/lib/types/database.types";
import { Group } from "@/src/models/group";

const logger = createLogger("base-repository");

export type FilterCondition = {
  field: string;
  operator: "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "in" | "nin" | "like" | "ilike" | "is" | "isNot";
  value: any;
};

export type FilterSchema = {
  conditions: FilterCondition[];
  logicalOperator?: "and" | "or";
};

export type SortSchema = {
  field: string;
  ascending?: boolean;
  nullsFirst?: boolean;
};

// Filters to tables with the full set of standard columns used by BaseRepository methods.
// Excludes join/pivot tables (e.g. file_sections_tasks, referral_appointment) and schema tables
// that lack these columns. Without this filter the union of all table Row types collapses the
// keyof intersection to near-nothing, breaking column-name validation in every query method.
export type StandardTableName = {
  [K in keyof Database["public"]["Tables"]]: Database["public"]["Tables"][K]["Row"] extends {
    id: string;
    group_id: string | null;
    updated_at: string | null;
    deleted_at: string | null;
  }
    ? K
    : never;
}[keyof Database["public"]["Tables"]];

/** Subset of {@link StandardTableName} where the row includes `display_id` (needed for typed `.eq("display_id", …)`). */
export type StandardTableNameWithDisplayId = {
  [K in StandardTableName]: "display_id" extends keyof Database["public"]["Tables"][K]["Row"] ? K : never;
}[StandardTableName];

type StandardInsert = Database["public"]["Tables"][StandardTableName]["Insert"];
type StandardUpdate = Database["public"]["Tables"][StandardTableName]["Update"];

export type BaseRepositoryOptions<T, R> = {
  // Accepts any table name; methods internally cast to StandardTableName for typed column access.
  tableName: StandardTableName;
  selectClause: string;
  mapRowToModel?: (row: R) => T;
  mapModelToRow?: (model: T) => R;
  modelShape?: Partial<T>;
};

/**
 * Default mapper that converts snake_case database rows to camelCase model objects
 */
export function defaultMapRowToModel<T, R>(row: R, modelShape?: Partial<T>): T {
  const mappedFields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
    mappedFields[customCamelCase(key)] = value;
  }

  if (modelShape) {
    return enforceShape(mappedFields, modelShape) as T;
  }

  return mappedFields as unknown as T;
}

/**
 * Default mapper that converts camelCase model objects to snake_case database rows
 */
export function defaultMapModelToRow<T, R>(model: T, modelShape?: Partial<T>): R {
  let cleaned = model;

  if (modelShape) {
    cleaned = enforceShape(model, modelShape) as T;
  }

  const mappedFields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(cleaned as Record<string, unknown>)) {
    mappedFields[customSnakeCase(key)] = value;
  }

  return mappedFields as unknown as R;
}

export class BaseRepository<T, R extends Database["public"]["Tables"][keyof Database["public"]["Tables"]]["Row"]> {
  protected options: BaseRepositoryOptions<T, R>;
  protected mapRowToModel: (row: R) => T;
  protected mapModelToRow: (model: T) => R;

  constructor(options: BaseRepositoryOptions<T, R>) {
    this.options = options;

    // Use provided mappers or default case conversion mappers
    this.mapRowToModel = options.mapRowToModel || ((row: R) => defaultMapRowToModel<T, R>(row, options.modelShape));

    this.mapModelToRow = options.mapModelToRow || ((model: T) => defaultMapModelToRow<T, R>(model, options.modelShape));
  }

  private applyFilterSchema(query: any, filterSchema: FilterSchema): any {
    if (!filterSchema || !filterSchema.conditions || !Array.isArray(filterSchema.conditions)) {
      return query;
    }

    const { conditions, logicalOperator = "and" } = filterSchema;

    return conditions.reduce((acc, condition) => {
      const { field, operator, value } = condition;

      switch (operator) {
        case "eq":
          return logicalOperator === "or" ? acc.or(`${field}.eq.${value}`) : acc.eq(field, value);
        case "neq":
          return logicalOperator === "or" ? acc.or(`${field}.neq.${value}`) : acc.neq(field, value);
        case "gt":
          return logicalOperator === "or" ? acc.or(`${field}.gt.${value}`) : acc.gt(field, value);
        case "gte":
          return logicalOperator === "or" ? acc.or(`${field}.gte.${value}`) : acc.gte(field, value);
        case "lt":
          return logicalOperator === "or" ? acc.or(`${field}.lt.${value}`) : acc.lt(field, value);
        case "lte":
          return logicalOperator === "or" ? acc.or(`${field}.lte.${value}`) : acc.lte(field, value);
        case "in":
          return logicalOperator === "or" ? acc.or(`${field}.in.(${value.join(",")})`) : acc.in(field, value);
        case "nin":
          return logicalOperator === "or"
            ? acc.or(`${field}.not.in.(${value.join(",")})`)
            : acc.not(field, "in", value);
        case "like":
          return logicalOperator === "or" ? acc.or(`${field}.like.${value}`) : acc.like(field, value);
        case "ilike":
          return logicalOperator === "or" ? acc.or(`${field}.ilike.${value}`) : acc.ilike(field, value);
        case "is":
          return logicalOperator === "or" ? acc.or(`${field}.is.${value}`) : acc.is(field, value);
        case "isNot":
          return logicalOperator === "or" ? acc.or(`${field}.not.is.${value}`) : acc.not(field, "is", value);
        default:
          return acc;
      }
    }, query);
  }

  public async getWithFilter({
    id,
    group,
    filterSchema,
  }: {
    id: string;
    group: Group;
    filterSchema?: FilterSchema | null;
  }): Promise<T | null> {
    const start = performance.now();

    if (!id) return null;

    const { data, error } = await withRetry(`get from ${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      let query = supabaseClient
        .from(this.options.tableName as StandardTableName)
        .select(this.options.selectClause)
        .eq("id", id)
        .eq("group_id", group.id);

      if (filterSchema) {
        query = this.applyFilterSchema(query, filterSchema);
      }

      const { data: result, error: dbError } = await query;

      if (dbError) {
        logger.warn(`Error getting record for ${this.options.tableName}`, {
          tableName: this.options.tableName,
          id,
          error: dbError,
        });

        return { data: null, error: dbError };
      }

      return { data: result, error: null };
    });

    if (error) {
      throw error;
    }

    if (!data || data.length !== 1) return null;

    const result = this.mapRowToModel(data![0] as unknown as R);

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.get.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async get(id: string, group: Group): Promise<T | null> {
    const start = performance.now();

    if (!id) return null;

    const { data, error } = await withRetry(`get from ${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      const { data: result, error: dbError } = await supabaseClient
        .from(this.options.tableName as StandardTableName)
        .select(this.options.selectClause)
        .eq("id", id)
        .eq("group_id", group.id)
        .is("deleted_at", null);

      if (dbError) {
        logger.warn(`Error getting record for ${this.options.tableName}`, {
          tableName: this.options.tableName,
          id,
          error: dbError,
        });

        return { data: null, error: dbError };
      }

      return { data: result, error: null };
    });

    if (error) {
      throw error;
    }

    if (!data || data.length !== 1) return null;

    const result = this.mapRowToModel(data![0] as unknown as R);

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.get.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async getByDisplayId(displayId: string, group: Group): Promise<T | null> {
    const start = performance.now();

    if (!displayId) return null;

    const { data, error } = await withRetry(`getByDisplayId from ${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      const { data: result, error: dbError } = await supabaseClient
        .from(this.options.tableName as StandardTableNameWithDisplayId)
        .select(this.options.selectClause)
        .eq("display_id", displayId)
        .eq("group_id", group.id)
        .is("deleted_at", null);

      if (dbError) {
        logger.warn(`Error getting record for ${this.options.tableName}`, {
          tableName: this.options.tableName,
          displayId,
          error: dbError,
        });

        return { data: null, error: dbError };
      }

      return { data: result, error: null };
    });

    if (error) {
      throw error;
    }

    if (!data || data.length !== 1) return null;

    const result = this.mapRowToModel(data![0] as unknown as R);

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.getByDisplayId.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async getByField(fieldName: string, fieldValue: string | number | boolean | null, group: Group): Promise<T[]> {
    if (fieldValue === null || fieldValue === undefined) return [];

    const { data, error } = await withRetry(`getByField from ${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      const { data: result, error: dbError } = await supabaseClient
        .from(this.options.tableName as StandardTableName)
        .select(this.options.selectClause)
        .eq(fieldName, fieldValue)
        .eq("group_id", group.id)
        .is("deleted_at", null);

      if (dbError) {
        logger.warn(`Error getting record for ${this.options.tableName}`, { error: dbError, fieldName, fieldValue });

        return { data: null, error: dbError };
      }

      return { data: result, error: null };
    });

    if (error) {
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    return data.map((row) => this.mapRowToModel(row as unknown as R));
  }

  public async insert(model: T, group: Group): Promise<T> {
    const start = performance.now();
    const modelExists = await this.get((model as any).id, group);

    if (modelExists) {
      const end = performance.now();

      stats.histogram(`${this.options.tableName}.insert.duplicate.duration`, end - start, {
        tags: {
          groupId: group.id,
        },
      });

      return modelExists;
    }

    (model as any).groupId = group.id;

    const { data, error } = await withRetry(`insert into ${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      const { data: result, error: dbError } = await supabaseClient
        .from(this.options.tableName as StandardTableName)
        .insert(this.mapModelToRow(model) as any)
        .select(this.options.selectClause);

      if (dbError) {
        logger.warn(`Error inserting record for ${this.options.tableName}`, {
          tableName: this.options.tableName,
          model,
          error: dbError,
        });

        return { data: null, error: dbError };
      }

      if (!result || result.length === 0) {
        const err = new Error(`No data returned from insert into ${this.options.tableName} ${(model as any).id}`);

        return { data: null, error: err };
      }

      return { data: result, error: null };
    });

    if (error) {
      throw error;
    }

    const result = this.mapRowToModel(data![0] as unknown as R);

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.insert.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async update(id: string, partialModel: Partial<T>, group: Group): Promise<T> {
    const start = performance.now();

    const { data, error } = await withRetry(`update into ${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      const { data: existingData, error: fetchError } = await supabaseClient
        .from(this.options.tableName as StandardTableName)
        .select(this.options.selectClause)
        .eq("id", id)
        .eq("group_id", group.id)
        .is("deleted_at", null);

      if (fetchError) {
        throw fetchError;
      }

      if (!existingData || existingData.length !== 1) {
        throw new Error(`Model with id ${id} not found in ${this.options.tableName}`);
      }

      const existingModel = this.mapRowToModel(existingData[0] as unknown as R);
      const updatedAt = (existingModel as any).updatedAt;

      const updatedModel = { ...existingModel, ...partialModel };

      (updatedModel as any).groupId = group.id;
      (updatedModel as any).updatedAt = new Date().toISOString();

      const query = supabaseClient
        .from(this.options.tableName as StandardTableName)
        .update(this.mapModelToRow(updatedModel) as unknown as StandardUpdate)
        .eq("id", id)
        .eq("group_id", group.id);

      if (updatedAt) {
        query.eq("updated_at", updatedAt);
      }

      const result = await query.select(this.options.selectClause);

      // Check for database errors first
      if (result.error) {
        logger.warn(`Error updating record for ${this.options.tableName}`, {
          tableName: this.options.tableName,
          id,
          error: result.error,
        });

        return { data: null, error: result.error };
      }

      // Check for optimistic lock conflict
      if (!result.data || result.data.length === 0) {
        const err = new Error("Optimistic lock conflict - record was modified");

        Object.assign(err, { code: "OPTIMISTIC_LOCK_CONFLICT" });

        return { data: null, error: err };
      }

      return { data: result.data, error: null };
    });

    if (error || !data || data.length === 0) {
      logger.logAndThrow(`Error updating record for ${this.options.tableName}`, {
        error,
        inputModel: partialModel,
      });
    }

    const result = this.mapRowToModel(data![0] as unknown as R);

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.update.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async delete(model: T, group: Group): Promise<boolean> {
    const start = performance.now();
    const { data: _data, error } = await withRetry(`delete${this.options.tableName}`, async () => {
      const supabaseClient = await getSupabaseClient();

      const query = supabaseClient
        .from(this.options.tableName as StandardTableName)
        .update({ deleted_at: new Date().toISOString() } as unknown as StandardUpdate)
        .eq("id", (model as any).id)
        .eq("group_id", group.id);

      const { data: result, error: dbError } = await query.select(this.options.selectClause);

      if (dbError) {
        logger.warn(`Error deleting from ${this.options.tableName}`, {
          tableName: this.options.tableName,
          error: dbError,
          id: (model as any).id,
        });

        return { data: null, error: dbError };
      }

      if (!result || result.length === 0) {
        const err = new Error(`No data returned from delete ${this.options.tableName} ${(model as any).id}`);

        return { data: null, error: err };
      }

      return { data: result, error: null };
    });

    if (error) {
      throw error;
    }

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.delete.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return true;
  }

  public async getAll(group: Group, filterSchema?: FilterSchema, sortSchema?: SortSchema): Promise<T[]> {
    const start = performance.now();
    const result = await getAllItems({
      tableName: this.options.tableName,
      selectClause: this.options.selectClause,
      mapRowToModel: this.mapRowToModel,
      filter: (query) => {
        let filteredQuery = query.eq("group_id", group.id).is("deleted_at", null);

        if (filterSchema) {
          filteredQuery = this.applyFilterSchema(filteredQuery, filterSchema);
        }

        if (sortSchema) {
          filteredQuery = filteredQuery.order(sortSchema.field, {
            ascending: sortSchema.ascending ?? false,
            nullsFirst: sortSchema.nullsFirst ?? false,
          });
        }

        return filteredQuery;
      },
    });

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.getAll.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async getAllInRange(group: Group, dateFrom: string, dateTo: string): Promise<T[]> {
    const start = performance.now();
    const result = await getAllItems({
      tableName: this.options.tableName,
      selectClause: this.options.selectClause,
      mapRowToModel: this.mapRowToModel,
      filter: (query) => {
        return query
          .eq("group_id", group.id)
          .gte("created_at", dateFrom)
          .lte("created_at", dateTo)
          .is("deleted_at", null);
      },
    });

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.getAllInRange.duration`, end - start, {
      tags: {
        groupId: group.id,
      },
    });

    return result;
  }

  public async bulkInsert(
    models: T[],
    group: Group,
    options?: { skipDuplicates?: boolean; batchSize?: number }
  ): Promise<T[]> {
    const start = performance.now();
    const { skipDuplicates = true, batchSize = 1000 } = options || {};

    if (models.length === 0) {
      return [];
    }

    // Set groupId for all models
    models.forEach((model) => {
      (model as any).groupId = group.id;
    });

    // Convert models to rows
    const rows = models.map((model) => this.mapModelToRow(model));

    const results: T[] = [];

    // Process in batches to avoid hitting database limits
    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);

      const { data, error } = await withRetry(
        `bulk insert batch ${i / batchSize + 1} into ${this.options.tableName}`,
        async () => {
          const supabaseClient = await getSupabaseClient();

          let result;

          if (skipDuplicates) {
            // ignoreDuplicates makes conflicts a no-op (ON CONFLICT DO NOTHING). A plain
            // upsert would DO UPDATE, silently reassigning rows owned by another record
            // (e.g. stealing a referral_appointment link). Skipped rows are not returned.
            result = await supabaseClient
              .from(this.options.tableName as StandardTableName)
              .upsert(batch as unknown as StandardInsert[], { ignoreDuplicates: true })
              .select(this.options.selectClause);
          } else {
            result = await supabaseClient
              .from(this.options.tableName as StandardTableName)
              .insert(batch as unknown as StandardInsert[])
              .select(this.options.selectClause);
          }

          return { data: result.data, error: result.error };
        }
      );

      if (error) {
        logger.warn(`Error in bulk insert batch ${i / batchSize + 1}`, {
          tableName: this.options.tableName,
          batchStart: i,
          batchSize: batch.length,
          error,
        });

        throw error;
      }

      if (!data) {
        throw new Error(`No insert result returned for ${this.options.tableName}`);
      }

      if (data.length > 0) {
        const batchResults = data.map((row) => this.mapRowToModel(row as unknown as R));

        results.push(...batchResults);
      }
    }

    const end = performance.now();

    stats.histogram(`${this.options.tableName}.bulkInsert.duration`, end - start, {
      tags: {
        groupId: group.id,
        totalItems: models.length,
        successfulItems: results.length,
        batchCount: Math.ceil(rows.length / batchSize),
      },
    });

    stats.histogram(`${this.options.tableName}.bulkInsert.itemsPerSecond`, models.length / ((end - start) / 1000), {
      tags: {
        groupId: group.id,
      },
    });

    return results;
  }
}
